import { useState, useEffect, useCallback } from 'react';
import {
  StudentSearchFilters,
  StudentSearchResult,
  PaginationInfo,
  StudentSortOption,
} from '../types/student-search';
import { studentSearchService } from '../services/studentSearchService';
import { useToast } from './useToast';

interface UseStudentSearchState {
  students: StudentSearchResult[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  selectedStudents: number[];
  searchFilters: StudentSearchFilters;
}

const initialFilters: StudentSearchFilters = {
  sort: StudentSortOption.LAST_UPDATED,
  order: 'desc',
  page: 1,
};

const initialPagination: PaginationInfo = {
  page: 1,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

export const useStudentSearch = () => {
  const [state, setState] = useState<UseStudentSearchState>({
    students: [],
    pagination: null,
    loading: false,
    error: null,
    selectedStudents: [],
    searchFilters: initialFilters,
  });

  const { showToast } = useToast();

  // 学生検索実行
  const searchStudents = useCallback(
    async (filters: StudentSearchFilters = state.searchFilters) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await studentSearchService.searchStudents(filters);
        setState((prev) => ({
          ...prev,
          students: response.data.students,
          pagination: response.data.pagination,
          searchFilters: filters,
          loading: false,
          selectedStudents: [], // 検索時に選択をリセット
        }));
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : '学生検索に失敗しました';
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
        showToast(errorMessage, 'error');
      }
    },
    [state.searchFilters, showToast]
  );

  // 検索フィルター更新
  const updateFilters = useCallback(
    (newFilters: Partial<StudentSearchFilters>) => {
      const updatedFilters = {
        ...state.searchFilters,
        ...newFilters,
        page: newFilters.page !== undefined ? newFilters.page : 1, // ページ以外の変更時は1ページ目にリセット
      };
      setState((prev) => ({
        ...prev,
        searchFilters: updatedFilters,
      }));
    },
    [state.searchFilters]
  );

  // ページ変更
  const changePage = useCallback(
    (page: number) => {
      const newFilters = { ...state.searchFilters, page };
      searchStudents(newFilters);
    },
    [state.searchFilters, searchStudents]
  );

  // 学生選択
  const toggleStudentSelection = useCallback((studentId: number) => {
    setState((prev) => ({
      ...prev,
      selectedStudents: prev.selectedStudents.includes(studentId)
        ? prev.selectedStudents.filter((id) => id !== studentId)
        : [...prev.selectedStudents, studentId],
    }));
  }, []);

  // 選択解除
  const clearSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedStudents: [],
    }));
  }, []);

  // 検索リセット
  const resetSearch = useCallback(() => {
    setState((prev) => ({
      ...prev,
      students: [],
      pagination: null,
      error: null,
      selectedStudents: [],
      searchFilters: initialFilters,
    }));
  }, []);

  // 初期検索実行を停止（too many API対策）
  // useEffect(() => {
  //   searchStudents();
  // }, []); // 初期表示での自動検索を無効化

  return {
    // 状態
    students: state.students,
    pagination: state.pagination,
    loading: state.loading,
    error: state.error,
    selectedStudents: state.selectedStudents,
    searchFilters: state.searchFilters,

    // アクション
    searchStudents,
    updateFilters,
    changePage,
    toggleStudentSelection,
    clearSelection,
    resetSearch,
  };
};
